import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

/**
 * Knowing when a deploy has left this tab behind, and moving the tab onto it
 * without costing the user anything.
 *
 * Every build writes its id to /version.json (see vite.config.ts) and bakes the
 * same id into the bundle. A tab whose baked id is older than the served one is
 * running code that is no longer deployed.
 *
 * Such a tab is not asked anything. The next time the user goes to another
 * page it loads that page fresh instead of rendering it in place, which looks
 * like any other page load and costs nothing: leaving a page already discards
 * whatever was typed into it. A tab that sits on one page for hours gets a
 * dismissible banner instead. Only a build marked `required` (an incompatible
 * API change) still gets the blocking modal.
 *
 * Every one of those must wait out work a reload would throw away: an upload
 * or submit still on the wire, or an AI extraction the user is watching. Those
 * make the guard busy.
 *
 * Some of that work outlives the component that knows about it. A finished
 * extraction is the case: the run is over, but the user has not yet reached the
 * draft it produced, and reloading them off the completion screen loses the
 * thread. The screen that knows the run finished unmounts on the way to the
 * draft, so it cannot hold the prompt back itself — hence deferrals, which one
 * component opens by name and another closes.
 */

export const VERSION_PATH = '/version.json';

const BUILD_ID = /^\d+$/;

/** The build id out of a /version.json body, or null for anything else. */
export const parseBuildId = (body: unknown): string | null => {
  if (!body || typeof body !== 'object') return null;
  const id = (body as { buildId?: unknown }).buildId;
  return typeof id === 'string' && BUILD_ID.test(id) ? id : null;
};

/**
 * Build ids are the build's epoch milliseconds, so they order. Newer, not
 * merely different: during a rolling deploy old and new pods answer side by
 * side, and a tab that reloaded onto an old pod must not be told to "update"
 * back to the build it just left.
 */
export const isNewerBuild = (running: string, deployed: string | null): boolean =>
  deployed !== null && BUILD_ID.test(running) && Number(deployed) > Number(running);

export type DeployedVersion = { buildId: string; required: boolean };

/** A /version.json body; `required` is only ever an explicit `true`. */
export const parseVersion = (body: unknown): DeployedVersion | null => {
  const buildId = parseBuildId(body);
  if (buildId === null) return null;
  return { buildId, required: (body as { required?: unknown }).required === true };
};

/** How long a tab may sit on an old build before the banner suggests a reload. */
export const BANNER_AFTER_MS = 4 * 60 * 60 * 1000;

export type UpdateState = {
  /** The newest build seen that is newer than this tab's, or null when current. */
  deployed: string | null;
  /** True once any build seen since this tab's is marked required. */
  required: boolean;
  /** When this tab first saw a newer build. */
  detectedAt: number | null;
  /** The user closed the banner for `deployed`; a later build reopens it. */
  dismissed: boolean;
};

type ReloadStorage = Pick<Storage, 'getItem' | 'setItem'>;

const RELOAD_KEY = 'appUpdateReloadTarget';

const sessionStore = (): ReloadStorage | undefined =>
  typeof sessionStorage === 'undefined' ? undefined : sessionStorage;

export const createUpdateStatus = (
  running: string,
  { now = Date.now, storage = sessionStore }: { now?: () => number; storage?: () => ReloadStorage | undefined } = {}
) => {
  let dismissedBuild: string | null = null;
  let state: UpdateState = { deployed: null, required: false, detectedAt: null, dismissed: false };
  const listeners = new Set<() => void>();

  const set = (next: Omit<UpdateState, 'dismissed'>) => {
    state = { ...next, dismissed: dismissedBuild !== null && !isNewerBuild(dismissedBuild, next.deployed) };
    for (const listener of listeners) listener();
  };

  return {
    report(version: DeployedVersion) {
      if (!isNewerBuild(running, version.buildId)) return;
      const newer = state.deployed === null || isNewerBuild(state.deployed, version.buildId);
      // Sticky: a later ordinary build does not make this tab's old code any
      // more compatible with the API the required one shipped with.
      const required = state.required || version.required;
      if (!newer && required === state.required) return;
      set({
        deployed: newer ? version.buildId : state.deployed,
        required,
        detectedAt: state.detectedAt ?? now(),
      });
    },

    dismiss() {
      if (state.deployed === null || state.dismissed) return;
      dismissedBuild = state.deployed;
      set(state);
    },

    /**
     * True when a navigation may reload onto `deployed`, recording the attempt.
     *
     * One attempt per target build per tab. A reload that lands on an old pod
     * mid-rollout, or on an index.html some cache kept, would otherwise reload
     * again on every navigation; after one try the tab falls back to the
     * banner. Without session storage the attempt cannot be recorded, so it is
     * never made.
     */
    claimReload(): boolean {
      if (state.deployed === null) return false;
      try {
        const store = storage();
        if (!store || store.getItem(RELOAD_KEY) === state.deployed) return false;
        store.setItem(RELOAD_KEY, state.deployed);
        return true;
      } catch {
        return false;
      }
    },

    getState(): UpdateState {
      return state;
    },

    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

export type UpdateStatus = ReturnType<typeof createUpdateStatus>;

/** The banner is for a tab left on an old build for hours, never a required one. */
export const isBannerDue = (state: UpdateState, at: number): boolean =>
  state.deployed !== null &&
  !state.required &&
  !state.dismissed &&
  state.detectedAt !== null &&
  at - state.detectedAt >= BANNER_AFTER_MS;

/** Whether leaving the current page should load the next one from the new build. */
export const shouldReloadOnNavigate = (state: UpdateState, busy: boolean): boolean => state.deployed !== null && !busy;

const WRITE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

/**
 * Longer than the 5-minute axios timeout, so a real request always settles
 * first. Only a write whose settle was never observed ages out, instead of
 * holding the prompt back for the life of the tab.
 */
export const STALE_WRITE_MS = 10 * 60 * 1000;

export const createUpdateGuard = (now: () => number = Date.now) => {
  const writes = new Map<number, number>();
  const holds = new Set<symbol>();
  const deferrals = new Map<string, number>();
  const listeners = new Set<() => void>();
  let nextWriteId = 1;

  const emit = () => {
    for (const listener of listeners) listener();
  };

  return {
    beginWrite(): number {
      const id = nextWriteId++;
      writes.set(id, now());
      emit();
      return id;
    },

    endWrite(id: number) {
      if (writes.delete(id)) emit();
    },

    /**
     * Keeps the prompt back until `clearDeferral(key)`, or until ttlMs has
     * passed — whichever comes first.
     *
     * The expiry is the point: a handover across a navigation is not
     * guaranteed to happen. A user who closes the extraction screen and goes
     * somewhere else entirely would otherwise hold the prompt back for the
     * life of the tab, which is worse than being asked to reload a little
     * early. Re-opening an existing key extends it rather than stacking.
     */
    defer(key: string, ttlMs: number) {
      deferrals.set(key, now() + ttlMs);
      emit();
    },

    clearDeferral(key: string) {
      if (deferrals.delete(key)) emit();
    },

    /** Keeps the prompt back until the returned release is called. */
    hold(): () => void {
      const token = Symbol('app-update-hold');
      holds.add(token);
      emit();
      return () => {
        if (holds.delete(token)) emit();
      };
    },

    isBusy(): boolean {
      if (holds.size > 0) return true;
      const at = now();
      for (const [key, expiry] of deferrals) {
        if (expiry > at) return true;
        deferrals.delete(key);
      }
      const cutoff = at - STALE_WRITE_MS;
      for (const [id, startedAt] of writes) {
        if (startedAt >= cutoff) return true;
        writes.delete(id);
      }
      return false;
    },

    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

export type UpdateGuard = ReturnType<typeof createUpdateGuard>;

export const updateGuard = createUpdateGuard();

// Undefined outside a Vite build (the unit tests); an empty id is never behind.
export const updateStatus = createUpdateStatus(typeof __APP_BUILD_ID__ === 'string' ? __APP_BUILD_ID__ : '');

type TrackedConfig = InternalAxiosRequestConfig & { appUpdateWriteId?: number };

/**
 * Counts an instance's writes as busy while they are in flight.
 *
 * Register it after the instance's own interceptors: request interceptors run
 * last-registered-first, so the write is counted before anything else can
 * reject it. The id travels on the config, which axios copies into a retried
 * request (the 401 refresh path), so a retry neither counts twice nor drops the
 * original.
 */
export const trackPendingWrites = (instance: Pick<AxiosInstance, 'interceptors'>, guard: UpdateGuard = updateGuard) => {
  const settle = (config: TrackedConfig | undefined) => {
    if (config?.appUpdateWriteId === undefined) return;
    guard.endWrite(config.appUpdateWriteId);
    config.appUpdateWriteId = undefined;
  };

  instance.interceptors.request.use((config: TrackedConfig) => {
    if (config.appUpdateWriteId === undefined && WRITE_METHODS.has((config.method ?? 'get').toLowerCase())) {
      config.appUpdateWriteId = guard.beginWrite();
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      settle(response.config as TrackedConfig);
      return response;
    },
    (error) => {
      settle(error?.config);
      return Promise.reject(error);
    }
  );
};
